// Pure, DOM-free logic shared by ArabicKeyboard.tsx and its test suite (tests/arabicKeyboardCore.test.ts).
//
// Nothing here reverses, reorders, or shapes Arabic text. Insertion is always a plain splice
// at a character offset -- the browser's own Unicode bidi/shaping engine is what renders
// combining marks (harakat) attached to their preceding base letter; this module never needs
// to know that ع + kasrah "looks like" عِ, it only needs to place the kasrah codepoint
// immediately after the ع codepoint in the underlying string, in that logical order. That is
// exactly what spliceInsert does, called once per key click in the order clicked -- there is
// no reversal/reordering logic anywhere in this file or in ArabicKeyboard.tsx.
//
// ArabicKeyboard.tsx's live runtime insertion goes through document.execCommand
// ('insertText'/'delete') rather than calling spliceInsert/spliceDelete directly, because
// execCommand is what correctly keeps a Qirtaas/ProseMirror contenteditable surface in sync
// with its own internal document model, and also correctly keeps a React-controlled
// <input>/<textarea> in sync (both listen for the native 'input' event execCommand
// dispatches). spliceInsert/spliceDelete are the plain-splice MODEL of exactly what that
// native insertion is specified to do at a given cursor/selection -- verified here
// deterministically, without a browser -- and are also used directly, live, for the
// harakah-attachment preview (charBeforeCursor), which only ever reads state, never mutates
// the editor's DOM.

export function spliceInsert(text: string, start: number, end: number, insertion: string): { text: string; caret: number } {
  const next = text.slice(0, start) + insertion + text.slice(end)
  return { text: next, caret: start + insertion.length }
}

// Mirrors native Backspace / execCommand('delete'): removes the current selection if there is
// one, otherwise removes exactly one UTF-16 code unit before the cursor. Arabic base letters
// and harakat are all single (non-surrogate-pair) BMP code units, and a harakah is always
// inserted immediately after its base letter -- so when the cursor sits right after a
// harakah, this removes only the harakah, leaving the base letter untouched.
export function spliceDelete(text: string, start: number, end: number): { text: string; caret: number } {
  if (start !== end) return { text: text.slice(0, start) + text.slice(end), caret: start }
  if (start > 0) return { text: text.slice(0, start - 1) + text.slice(start), caret: start - 1 }
  return { text, caret: start }
}

// Read-only: the single character immediately before the cursor (or before the start of a
// selection), for the harakah-attachment preview. Never mutates anything.
export function charBeforeCursor(text: string, cursor: number): string {
  return cursor > 0 ? text.slice(cursor - 1, cursor) : ''
}